import React, { useState } from 'react';

import Button from '@amzn/meridian/button';
import ControlGroup from '@amzn/meridian/control-group';
import { ModalFooter } from '@amzn/meridian/modal';
import RadioButton from '@amzn/meridian/radio-button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { INDEFINITE_RESTRICTIONS_ID, RETURN_TO_FULL_DUTY_ID } from 'src/config/AccommodationStatusId';
import { APPROVED_TWP, DENIED_TWP, DLS_REFERRAL, INDEFINITE_RESTRICTIONS } from 'src/config/AccommodationStatusNames';
import PlacementDetailsService from 'src/services/placementDetailsService';
import {
  getPlacementDetails,
  setIsUpdateStatusModalOpen,
} from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import {
  type AccommodationStatus,
  type AccommodationStatusWithDecisionByLoginNeeded,
} from 'src/store/placementDetailsPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { isCanadianSite } from 'src/utils/helpers';

import { UpdateStatusProgress } from '../constants';

interface StatusSelectionsProps {
  selectedStatus: AccommodationStatus | AccommodationStatusWithDecisionByLoginNeeded | undefined;
  setSelectedStatus: (val: AccommodationStatus | AccommodationStatusWithDecisionByLoginNeeded | undefined) => void;
  setProgress: (val: UpdateStatusProgress) => void;
}

// props isSiteParticipatingInDecisionHubPilot
const StatusSelection = ({ selectedStatus, setSelectedStatus, setProgress }: StatusSelectionsProps) => {
  const dispatch = useAppDispatch();
  const { featureFlags } = useAppSelector((state) => state.user);
  const {
    isSiteParticipatingInDecisionHubPilot,
    accommodationStatus,
    placementDetails: {
      accommodationType,
      accommodationStatus: placementCurrentStatus,
      associateFullName,
      login,
      countryId,
    },
    placementId,
  } = useAppSelector((state) => state.placementDetailsPage);
  const associateName: string = associateFullName ? associateFullName : login || '';

  const [showSpinner, setShowSpinner] = useState(false);

  const decisionHubEnabled = featureFlags.DECISION_PORTAL && isSiteParticipatingInDecisionHubPilot;
  // Only show RTFD and Indefinite Restrictions if current status is Indefinite Restrictions.
  // Refer: https://issues.amazon.com/issues/P113097040
  const notDecisionHubEnabledAndCurrentStatusIsIndefinite: boolean =
    !decisionHubEnabled && placementCurrentStatus?.status === INDEFINITE_RESTRICTIONS;

  const indefiniteRestrictionsTransitionSet = new Set([RETURN_TO_FULL_DUTY_ID, INDEFINITE_RESTRICTIONS_ID]);
  const selectableStatuses = notDecisionHubEnabledAndCurrentStatusIsIndefinite
    ? accommodationStatus?.filter((accStatus) => indefiniteRestrictionsTransitionSet.has(accStatus.id))
    : accommodationStatus;

  const isDecisionMakerNeeded: boolean = decisionHubEnabled
    ? !!selectedStatus &&
      (accommodationType === 'DALI' || accommodationType === 'BALI') &&
      [APPROVED_TWP, DENIED_TWP, INDEFINITE_RESTRICTIONS].includes(selectedStatus.status)
    : !!selectedStatus && !!(selectedStatus as AccommodationStatusWithDecisionByLoginNeeded).decisionByLoginNeeded;

  const isDlsReferralInfoNeeded = !isCanadianSite(countryId) && selectedStatus?.status == DLS_REFERRAL;

  const onSave = async () => {
    if (isDecisionMakerNeeded) {
      setProgress(UpdateStatusProgress.DecisionByLoginEntry);
    } else if (isDlsReferralInfoNeeded) {
      setProgress(UpdateStatusProgress.DlsReferralFormEntry);
    } else {
      setShowSpinner(true);
      try {
        const statusPayload = { accommodationId: placementId, accommodationStatusId: selectedStatus!.id };
        const updateStatusResponse = decisionHubEnabled
          ? await PlacementDetailsService.updateAccommodationStatusV2(statusPayload)
          : await PlacementDetailsService.updateAccommodationStatus(statusPayload);

        if (updateStatusResponse?.status === 200) {
          dispatch(getPlacementDetails(placementId));
          dispatch(setIsUpdateStatusModalOpen(false));
        }
      } finally {
        setShowSpinner(false);
      }
    }
  };

  return (
    <>
      <Text id="update-placement-modal-text">
        Update Placement Status for
        <b>{` ${associateName}`}</b>
      </Text>
      <Row widths="fill" spacingInset={'400 none'}>
        <LoadingSpinnerWrapper showSpinner={showSpinner}>
          <ControlGroup
            legend=""
            name="Placement Options"
            data-cy="placement-status__radio-btn-grp"
            onChange={(newStatus: string) => {
              setSelectedStatus(selectableStatuses?.find((val) => val.status == newStatus));
            }}
            value={selectedStatus?.status}
          >
            {selectableStatuses?.map((option, index) => (
              <RadioButton key={option.status} value={option.status} data-cy={`placement-status__${index}_radio-btn`}>
                {option.status}
              </RadioButton>
            ))}
          </ControlGroup>
        </LoadingSpinnerWrapper>
      </Row>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fill">
          <Button
            id="update-placement-status-cancel-btn"
            data-cy="placement-status__cancel-btn"
            type="secondary"
            size="small"
            disabled={showSpinner}
            onClick={() => dispatch(setIsUpdateStatusModalOpen(false))}
          >
            Cancel
          </Button>
          <Button
            id="update-placement-status-save-btn-v2"
            data-cy="placement-status__save_radio-btn"
            type="primary"
            size="small"
            disabled={!selectedStatus || showSpinner}
            onClick={onSave}
          >
            {isDecisionMakerNeeded || isDlsReferralInfoNeeded ? 'Next' : 'Update Status'}
          </Button>
        </Row>
      </ModalFooter>
    </>
  );
};

export default StatusSelection;
